/**
 * Status pill for an EHR connection. Colours come from the theme's semantic
 * palette so the chip follows light/dark mode.
 */
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import type { EhrSyncStatus } from '@models/ehr-sync';

import { statusMessages } from '../messages';
import type { EhrSyncTone } from './tone';
import { toneTextColor, toneTintColor } from './tone';

/** Props for {@link EhrSyncStatusChip}. */
interface EhrSyncStatusChipProps {
  status: EhrSyncStatus;
}

const STATUS_PALETTE: Record<EhrSyncStatus, EhrSyncTone> = {
  connected: 'success',
  degraded: 'warning',
  failed: 'error',
  pending: 'grey',
};

/**
 * Pill with a dot and the localized status label, tinted by status tone.
 *
 * @param props - see {@link EhrSyncStatusChipProps}
 */
export default function EhrSyncStatusChip({ status }: EhrSyncStatusChipProps) {
  const { formatMessage } = useIntl();
  const palette = STATUS_PALETTE[status];

  return (
    <Typography
      component="span"
      variant="caption"
      fontWeight="bold"
      sx={(theme) => {
        const color = toneTextColor(theme, palette);

        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.1,
          py: 0.4,
          borderRadius: 999,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color,
          backgroundColor: alpha(toneTintColor(theme, palette), 0.14),
        };
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
        }}
      />
      {formatMessage(statusMessages[status])}
    </Typography>
  );
}
